import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { MDXProvider } from "@mdx-js/react";
import InlineCTA from "./components/blog/InlineCTA";
import ErrorBoundary from "./components/ErrorBoundary";
import App from "./App.tsx";
import { initAnalytics } from "./lib/analytics";
import "./index.css";

// Fire PostHog init before the app mounts so page-view capture catches the
// initial navigation. No-op if VITE_POSTHOG_KEY isn't set (dev / pre-launch).
initAnalytics();

const mdxComponents = {
  InlineCTA,
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Only enable in production so local dev stays noise-free
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Capture 5% of sessions, 100% of sessions with errors
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground text-sm mb-6">
          {error instanceof Error ? error.message : "An unexpected error occurred"}
        </p>
        <div className="flex gap-3">
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-secondary transition-colors"
          >
            Try Again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm hover:opacity-90 transition-opacity"
          >
            Go Home
          </a>
        </div>
      </div>
    )}
  >
    <ErrorBoundary>
      <HelmetProvider>
        <MDXProvider components={mdxComponents}>
          <App />
        </MDXProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </Sentry.ErrorBoundary>
);
